import "@/App.css";
import { useLayoutEffect, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import Discover from "@/pages/Discover";
import SearchPage from "@/pages/SearchPage";
import CalendarPage from "@/pages/CalendarPage";
import EventDetails from "@/pages/EventDetails";
import SavedEvents from "@/pages/SavedEvents";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Signup from "@/pages/Signup";
import Profile from "@/pages/Profile";
import ResetPassword from "@/pages/ResetPassword";
import AddEvent from "@/pages/AddEvent";
import MyEvents from "@/pages/MyEvents";
import BoostResult from "@/pages/BoostResult";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Advertise from "@/pages/Advertise";
import DeleteAccountInfo from "@/pages/DeleteAccountInfo";
import MyBannerAds from "@/pages/MyBannerAds";
import Admin from "@/pages/Admin";
import AdminAgent from "@/pages/AdminAgent";
import { BottomNav } from "@/components/BottomNav";
import { RouteSeo } from "@/components/RouteSeo";
import { Toaster } from "@/components/ui/sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { CountryProvider } from "@/context/CountryContext";
import { applyStoredConsent } from "@/lib/analyticsConsent";
import { useRealViewportHeight } from "@/hooks/useRealViewportHeight";

// Scroll the window to the top on genuine new navigations (PUSH/REPLACE). Keyed by
// the primary/background location so opening or closing the EventDetails overlay
// (which changes location to /event/:id while the background page stays put) never
// scrolls the originating page, and POP is left alone so Back keeps native offsets.
function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();
  const state = location.state;
  const displayLocation = (state && state.backgroundLocation) || location;
  useLayoutEffect(() => {
    if (navType !== "POP") window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayLocation.key, navType]);
  return null;
}

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { recoveryMode } = useAuth();
  const backgroundLocation = location.state && location.state.backgroundLocation;
  const noNavPrefixes = ["/event/", "/login", "/signup", "/reset-password", "/add-event", "/edit-event", "/my-events", "/payment", "/privacy", "/terms", "/delete-account", "/advertise", "/admin"];
  const hideNav = noNavPrefixes.some((p) => location.pathname.startsWith(p));

  useRealViewportHeight();

  // Reapply the saved analytics preference to Consent Mode on load (no popup).
  useEffect(() => {
    applyStoredConsent();
  }, []);

  // A password-recovery session must always land on the Set New Password form,
  // never silently drop the user into the app.
  useEffect(() => {
    if (recoveryMode && location.pathname !== "/reset-password") {
      navigate("/reset-password", { replace: true });
    }
  }, [recoveryMode, location.pathname, navigate]);

  return (
    <div className="App mx-auto min-h-[100dvh] max-w-md bg-[#050505] md:max-w-none">
      <ScrollToTop />
      <RouteSeo />
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<Discover />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/saved" element={<SavedEvents />} />
        <Route path="/event/:id" element={<EventDetails />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/add-event" element={<AddEvent />} />
        <Route path="/edit-event/:id" element={<AddEvent />} />
        <Route path="/my-events" element={<MyEvents />} />
        <Route path="/payment/success" element={<BoostResult mode="success" />} />
        <Route path="/payment/cancel" element={<BoostResult mode="cancel" />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/delete-account" element={<DeleteAccountInfo />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/my-banner-ads" element={<MyBannerAds />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/agent" element={<AdminAgent />} />
      </Routes>
      {backgroundLocation && (
        <Routes>
          <Route path="/event/:id" element={<EventDetails />} />
        </Routes>
      )}
      {!hideNav && <BottomNav />}
      <Toaster position="top-center" theme={theme} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <CountryProvider>
        <Shell />
      </CountryProvider>
    </BrowserRouter>
  );
}

export default App;
